import React from 'react';

import { spacings } from '@lumapps/classnames';
import { Checkbox, CheckboxProps } from '@lumapps/lumx/react';

import { UseListFilter, useListFilter, UseListFilterOptions } from '../../hooks/useListFilter';
import { defaultGetId } from '../../utils/defaultGetId';
import { Filter, type FilterProps } from '../Filter';

export interface CheckboxFilterProps<T> extends FilterProps {
    /** list of options to display as checkbox */
    options: T[];
    /** determines how to render the react node for the checkbox */
    renderCheckbox: (entity: T) => React.ReactNode;
    /** custom props for the rendered checkbox */
    checkboxProps?: (entity: T) => Partial<CheckboxProps>;
    /** Callback to get the id for each filter.  */
    getId?: (entity: T) => string;
    /** default values */
    defaults?: Partial<UseListFilterOptions<T>>;
    /**
     *  USED INTERNALLY, DO NOT USE, INJECTED BY PARENT `Filters`
     */
    onSelected?: UseListFilter<T>['onSelected'];
    /**
     *  USED INTERNALLY, DO NOT USE, INJECTED BY PARENT `Filters`
     */
    isInSelection?: UseListFilter<T>['isInSelection'];
}

/**
 * Component that renders a checkbox filter, used in the `@lumapps/lumx-filter-and-sort` pattern.
 * Commonly used for showing boolean filters, such as whether an entity is active or not.
 *
 * @family Filters
 * @param CheckboxFilterProps
 * @returns CheckboxFilter
 */
export const CheckboxFilter = <T,>({
    options,
    renderCheckbox,
    onSelected,
    isInSelection,
    checkboxProps = () => ({}),
    getId = defaultGetId,
    ...props
}: CheckboxFilterProps<T>) => {
    return (
        <Filter {...props} isFieldset titleAs="heading">
            {options.map((option, index) => (
                <Checkbox
                    key={getId ? getId(option) : index}
                    className={spacings([
                        { type: 'margin', direction: 'right' },
                        { type: 'margin', direction: 'bottom' },
                    ])}
                    // Use onChange instead of onClick to allow label to be clickable
                    onChange={() => onSelected?.(option)}
                    isChecked={isInSelection?.(option)}
                    label={renderCheckbox(option)}
                    {...checkboxProps(option)}
                />
            ))}
        </Filter>
    );
};

Object.assign(CheckboxFilter, { hook: useListFilter });
